import React from "react";
import { motion } from "framer-motion";
import {
    HeartHandshake,
    MessageCircle,
    Brain,
    CalendarCheck,
} from "lucide-react";

const KLAIR_ICON_SRC = "/logos/klareet-white-mono.png";

const FEATURES = [
    {
        Icon: MessageCircle,
        title: "Listens like a friend",
        body: "No multiple-choice tests. A real back-and-forth conversation.",
    },
    {
        Icon: Brain,
        title: "Spots signals you'd miss",
        body: "Pattern matches your story against thousands of careers.",
    },
    {
        Icon: CalendarCheck,
        title: "Stays with you for 90 days",
        body: "Weekly nudges so the snapshot doesn't end up in a folder.",
    },
];

export function KlairIntro() {
    return (
        <div
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
            data-testid="klair-intro"
        >
            {/* Avatar (left on desktop, stacked first on mobile) */}
            <div className="order-1 lg:order-1 lg:col-span-5 flex justify-center">
                <div className="relative">
                    <div
                        className="relative w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] lg:w-[360px] lg:h-[360px] rounded-full overflow-hidden border border-klareet-bor bg-klareet-card grid place-items-center"
                        data-testid="klair-avatar"
                    >
                        <motion.div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "conic-gradient(from 0deg, rgba(20,184,166,0.45), rgba(13,148,136,0) 50%, rgba(59,130,246,0.25), rgba(20,184,166,0.45))",
                                filter: "blur(28px)",
                                opacity: 0.7,
                            }}
                            animate={{ rotate: 360 }}
                            transition={{
                                duration: 22,
                                repeat: Infinity,
                                ease: "linear",
                            }}
                            aria-hidden
                        />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(circle at center, rgba(20,184,166,0.25), transparent 60%)",
                            }}
                            aria-hidden
                        />
                        <motion.img
                            src={KLAIR_ICON_SRC}
                            alt="Klair avatar"
                            className="relative w-[55%] h-[55%] object-contain drop-shadow-[0_0_50px_rgba(20,184,166,0.55)]"
                            animate={{ y: [-3, 3, -3] }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            draggable={false}
                            width="200"
                            height="200"
                        />
                    </div>
                </div>
            </div>

            {/* Text (right on desktop) */}
            <div className="order-2 lg:order-2 lg:col-span-7">
                <div className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-klareet-T">
                    Introducing
                </div>
                <h2
                    className="mt-3 text-3xl md:text-5xl font-bold tracking-tight text-klareet-TEXT leading-[1.05]"
                    data-testid="klair-headline"
                >
                    Meet{" "}
                    <span className="text-klareet-T2">Klair</span>, your
                    career buddy.
                </h2>
                <p className="mt-6 text-base md:text-lg text-klareet-MUT leading-relaxed max-w-xl">
                    Klair isn't another assessment tool. Klair is the friend
                    who actually listens, asks the hard questions, spots the
                    signals you keep missing about yourself, and stays with
                    you long after, making sure you actually do the work.
                </p>
                <p className="mt-3 italic text-sm md:text-base text-klareet-MUT max-w-xl">
                    A real conversation. Not a quiz. A plan you'd actually
                    follow. Not a personality type.
                </p>

                <ul className="mt-8 space-y-4">
                    {FEATURES.map(({ Icon, title, body }, i) => (
                        <motion.li
                            key={title}
                            initial={{ opacity: 0, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.4,
                                delay: i * 0.07,
                                ease: [0.2, 0.8, 0.2, 1],
                            }}
                            className="flex items-start gap-4"
                        >
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-klareet-T/15 shrink-0">
                                <Icon
                                    size={18}
                                    strokeWidth={2}
                                    className="text-klareet-T2"
                                />
                            </span>
                            <div>
                                <div className="text-[15px] font-semibold tracking-tight text-klareet-TEXT">
                                    {title}
                                </div>
                                <div className="text-sm text-klareet-MUT">
                                    {body}
                                </div>
                            </div>
                        </motion.li>
                    ))}
                </ul>

                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4 }}
                    className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-T/40 bg-klareet-T/10 text-sm text-klareet-TEXT"
                >
                    <HeartHandshake size={16} className="text-klareet-T2" />
                    Always honest. Always with you.
                </motion.div>
            </div>
        </div>
    );
}
